import { DemoDockCreditSummary, DemoDockSkuId } from "../Credits/creditExports";
import { getDemoDockProxyUrl } from "../auth";
import type { EditablePageElement, ReplaceTextCommand } from "../processPrompt";

export type GenerateFeatureResponse = DemoDockCreditSummary & {
  success: boolean;
  data: string;
  commands: ReplaceTextCommand[];
};

const accountHeaders = {
  'Content-Type': 'application/json',
};

async function readJsonResponse(response: Response) {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      payload && typeof payload.error === 'string'
        ? payload.error
        : 'Demo Dock could not reach the credit service.';
    throw new Error(message);
  }

  return payload;
}

export async function generateFeature(
  userPrompt: string,
  pageContext: EditablePageElement[]
): Promise<GenerateFeatureResponse> {
  const response = await fetch(getDemoDockProxyUrl('/api/generate-feature'), {
    method: 'POST',
    credentials: 'include',
    headers: accountHeaders,
    body: JSON.stringify({ userPrompt, pageContext }),
  });

  const payload = await readJsonResponse(response);

  if (!payload || typeof payload.data !== 'string' || !Array.isArray(payload.commands)) {
    throw new Error('Demo Dock received an unexpected AI response.');
  }

  return payload;
}

export async function getCreditAccount(): Promise<DemoDockCreditSummary> {
  const response = await fetch(getDemoDockProxyUrl('/api/credits/account'), {
    credentials: 'include',
    headers: accountHeaders,
  });
  return readJsonResponse(response);
}

export async function setCreditPlan(skuId: DemoDockSkuId): Promise<DemoDockCreditSummary> {
  const response = await fetch(getDemoDockProxyUrl('/api/credits/plan'), {
    method: 'POST',
    credentials: 'include',
    headers: accountHeaders,
    body: JSON.stringify({ skuId }),
  });
  return readJsonResponse(response);
}

export async function spendDemoDockCredit(): Promise<DemoDockCreditSummary> {
  const response = await fetch(getDemoDockProxyUrl('/api/credits/spend'), {
    method: 'POST',
    credentials: 'include',
    headers: accountHeaders,
  });
  return readJsonResponse(response);
}
